export function userRoles(args = {}) {
  return {
    addingUser: false,
    updatingUser: false,
    userToAssign: null,
    manageableRoles: args.manageableRoles || [],
    workOsManagedRoles: args.workOsManagedRoles || [],
    roleOptions: args.roleOptions || [],
    users: args.users || [],
    roleFilter: "",
    searchFilter: "",
    currentUserId: args.currentUserId,
    oneSnapshotAdmin: args.oneSnapshotAdmin,
    oneWorkspaceAdmin: args.oneWorkspaceAdmin,

    get filteredUsers() {
      let filtered = this.users;
      if (this.roleFilter) {
        filtered = filtered.filter((u) =>
          (u.assigned_roles || []).includes(this.roleFilter),
        );
      }
      if (this.searchFilter) {
        const s = this.searchFilter.toLowerCase();
        filtered = filtered.filter(
          (u) =>
            (u.name ?? "").toLowerCase().includes(s) ||
            (u.email ?? "").toLowerCase().includes(s),
        );
      }
      return filtered;
    },

    setRoleFilter(val) {
      this.roleFilter = val;
    },

    setSearchFilter(val) {
      this.searchFilter = val;
    },

    shouldShowRole(role) {
      return (
        this.userToAssign &&
        (this.isRoleManageable(role) || this.isRoleAssigned(role))
      );
    },

    isRoleManageable(role) {
      return this.manageableRoles.includes(role);
    },

    isRoleAssigned(role) {
      return (
        this.userToAssign &&
        this.userToAssign.assigned_roles &&
        this.userToAssign.assigned_roles.includes(role)
      );
    },

    isWorkOsManaged(role) {
      return this.workOsManagedRoles.includes(role);
    },

    get hasWorkOsManagedVisibleRoles() {
      return this.visibleRoles.some((role) => this.isWorkOsManaged(role));
    },

    get hasUneditableRoles() {
      if (!this.userToAssign) return false;
      return (this.userToAssign.assigned_roles || []).some(
        (role) => !this.isRoleManageable(role),
      );
    },

    get shouldShowUpdateModal() {
      return (
        !this.addingUser && this.updatingUser && this.userToAssign !== null
      );
    },

    closeUpdateModal() {
      this.updatingUser = false;
      this.userToAssign = null;
    },

    closeAddUserModal() {
      this.addingUser = false;
    },

    get visibleRoles() {
      if (!this.userToAssign) return [];
      return this.roleOptions
        .map((r) => r.value)
        .filter((role) => this.shouldShowRole(role));
    },

    getRoleMeta(role) {
      return this.roleOptions.find((r) => r.value === role) || {};
    },

    roleDisplayName(str) {
      if (!str) return "";
      if (str === "datacloud_admin") return "Database admin";
      if (str === "admin") return "Snapshot admin";
      if (str === "finance_admin") return "Finance manager";
      return str.replace(/_/g, " ").replace(/^./, (l) => l.toUpperCase());
    },

    getCurrentAdminCounts() {
      return {
        workspace: this.users.filter((u) =>
          (u.assigned_roles || []).includes("workspace_admin"),
        ).length,
        snapshot: this.users.filter((u) =>
          (u.assigned_roles || []).includes("snapshot_admin"),
        ).length,
      };
    },

    canDisableRole(role, userId) {
      if (role === "finance_admin" && !window.currentUser.is_finance_admin) {
        return false;
      }

      if (!this.isRoleManageable(role) || this.isWorkOsManaged(role)) {
        return false;
      }

      const counts = this.getCurrentAdminCounts();
      const user = this.users.find((u) => u.id === userId);

      if (
        role === "workspace_admin" &&
        counts.workspace === 1 &&
        (user?.assigned_roles || []).includes("workspace_admin")
      ) {
        return false;
      }
      if (
        role === "snapshot_admin" &&
        counts.snapshot === 1 &&
        (user?.assigned_roles || []).includes("snapshot_admin")
      ) {
        return false;
      }
      return true;
    },

    isLastAdmin(role, userId) {
      const counts = this.getCurrentAdminCounts();
      const user = this.users.find((u) => u.id === userId);

      if (
        role === "workspace_admin" &&
        counts.workspace === 1 &&
        (user?.assigned_roles || []).includes("workspace_admin")
      ) {
        return true;
      }
      if (
        role === "snapshot_admin" &&
        counts.snapshot === 1 &&
        (user?.assigned_roles || []).includes("snapshot_admin")
      ) {
        return true;
      }
      return false;
    },

    getUserCountText() {
      const count = this.filteredUsers.length;
      return count + " user" + (count === 1 ? "" : "s");
    },

    init() {
      // Listen for user selection events
      this.$el.addEventListener("user-selected", (event) => {
        this.userToAssign = event.detail;
        this.updatingUser = true;
        this.addingUser = false;
      });

      // Listen for role updates to keep local state in sync
      this.$el.addEventListener("update-user-roles", (event) => {
        const { userId, roles } = event.detail;
        const userIndex = this.users.findIndex((u) => u.id === userId);
        if (userIndex !== -1) {
          this.users[userIndex].assigned_roles = [...roles];
        }
      });
    },
  };
}
